<template lang="pug">
v-container
  v-row
    v-col(cols="12")
      h1 Мониторинг
    v-col(cols="4" v-if="!isRegion")
      select-region(
      v-model="form.region_id"
      label="Выберите регион"
      filled
    )
    v-col(:cols="isRegion ? 6 : 4")
      select-mun(
        isEmailSettings
        v-model="form.municipality_id"
        label="Выберите муниципалитет"
        :region="form.region_id"
        :disabled="!form.region_id"
        filled
      )
    v-col(:cols="isRegion ? 6 : 4")
      v-select(
        label="Статус обращения"
        v-model="form.status"
        filled
        :items="statusItems"
        @keyup.enter="runSearch"
        clearable
      )
  v-card(outlined)
    smart-table.pt-2(
      :adapter="adapter"
      :headers="headers"
      :needToUpdateTable="needToUpdateTable"
      :row-class="checkIsOverdue"
    )
      template(#value--created_at="{ item }") {{ item.created_at |readableDateTime }}

      template(#search--status="{ searchKeys, runSearch}")
        v-select(
          v-model="searchKeys.status"
          prepend-inner-icon="mdi-magnify"
          outlined
          :items="statusItems"
          @keyup.enter="runSearch"
          hide-details
          dense
          clearable
        )

      template(#value--worker_id="{ item }")
        v-chip(v-if="item.worker_id" text label small :class="{ 'success': !item.worker_id }")
          | {{ ROLES_NAMES[cachedUser(item.worker_id).roles] || 'Можно забрать к себе'}}
      template(#value--project_id="{ item }")
        v-chip(
          small
          color="secondary"
          label
        ) {{ cachedProject(item.project_id).name }}
      template(#value--municipality_id="{ item }")
        v-chip(v-if="item.municipality_id" text label small)
          | {{ cachedMunicipality(item.municipality_id).name }}
      template(#value--view="{ item }")
        router-link(:to="`/application/${item.id}`")
          v-icon(color="primary" small) mdi-eye
      template(#value--status="{ item }")
        v-chip(small label :color="statusColor(item.status)")
          | {{ CLIENT_QUESTION_STATUS_TEXT[item.status] }}
      template(#value--question_category_name="{ item }")
        v-chip(
          small
          label
        ) {{ cachedCategory(item.question_category_id).name || item.question_category_name }}
</template>

<script>
import { CRUDQuestionCategory } from '@/services/API/_crud';
import { ROLES_NAMES } from '@/constants';
import { differenceInDays } from 'date-fns/esm';
import SmartTable from '@/components/SmartTable/index.vue';
import clientQuestionService from '@/services/API/client-questions';
import { CLIENT_QUESTION_STATUS, CLIENT_QUESTION_STATUS_TEXT } from '../constants';
import mixinDateTime from '@/mixins/mixinDateTime';
import { mapActions, mapGetters } from 'vuex';
import { CACHE_KEYS } from '@/store/modules/cache';
import SelectMun from '@/components/inputs/SelectMun.vue';
import SelectRegion from '@/components/inputs/SelectRegion.vue';
import SelectTemplateCategory from '@/components/inputs/SelectTemplateCategory.vue';

export default {
  components: {
    SelectTemplateCategory,
    SelectRegion,
    SmartTable,
    SelectMun,
  },
  mixins: [mixinDateTime],
  data() {
    return {
      form: {
        region_id: null,
        municipality_id:null,
        status: null,
      },

      headers: [
        {
          text: '',
          value: 'view',
        },
        {
          text: 'Номер заявки',
          value: 'id',
          sortable: true,
          searchable: true,
        },
        {
          text: 'Название проблемы',
          value: 'text',
          sortable: true,
          searchable: true,
        },
        {
          text: 'Категория',
          value: 'question_category_name',
          searchable: true,
        },
        {
          text: 'Дата заявки',
          value: 'created_at',
          sortable: true,
        },
        {
          text: 'Обращение пользователя',
          value: 'worker_id',
          sortable: true,
        },
        {
          text: 'Статус',
          value: 'status',
        },
        {
          text: 'Почта отправителя',
          sortable: true,
          value: 'email',
          searchable: true,
        },
        { text: 'Муниципалитет', value: 'municipality_id', sortable: true },
        { text: 'Проект', value: 'project_id', sortable: true },
      ],
      needToUpdateTable: false,
      ROLES_NAMES,
      CLIENT_QUESTION_STATUS,
      CLIENT_QUESTION_STATUS_TEXT,
    };
  },
  computed:{
    ...mapGetters({
      'cachedCategory': 'cache/cachedCategory',
      'cachedMunicipality':'cache/cachedMunicipality',
      'cachedProject':'cache/cachedProject',
      'user':'auth/getUser',
      'cachedUser' : 'cache/cachedUser',
      'isSupport': 'auth/isSupport',
      'isRegion': 'auth/isRegion',
    }),
    statusItems() {
      return [
        { value: 0, text: 'Все' },
        ...Object.keys(CLIENT_QUESTION_STATUS_TEXT).map((key) => ({
          value: Number(key),
          text: CLIENT_QUESTION_STATUS_TEXT[key],
        }))];
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.needToUpdateTable = true;
      },
    },
  },
  async created() {
    this.form.region_id = this.user.region_id;
    this.form.status = CLIENT_QUESTION_STATUS.PENDING;
    await this.fetchQuestionCategory();
  },
  methods: {
    ...mapActions({
      'loadToCache': 'cache/loadToCache',
    }),

    async adapter({ _update, ...options }) {
      const params = {
        'per-page': options.itemsPerPage,
        'sort': 'id',
        ...options,
      };

      this.needToUpdateTable = false;

      Object.keys(this.form).forEach((key) => {
        if (this.form[key])  params[`search[${key}]`] = this.form[key];
      });

      if (this.isRegion) delete params['search[region_id]'];

      const response = await clientQuestionService.monitoring(params);
      const usersIDs = response.data.data.map(({ worker_id }) => worker_id);
      await this.loadToCache({ key:CACHE_KEYS.users, ids:usersIDs });
      const municipality_ids = response.data.data.map(({ municipality_id }) => municipality_id);
      await this.loadToCache({ key: CACHE_KEYS.municipalities, ids: municipality_ids });

      const project_ids = response.data.data.map(({ project_id }) => project_id);
      await this.loadToCache({ key: CACHE_KEYS.projects, ids:  project_ids });

      return new Promise((resolve) => resolve(response));
    },
    async fetchQuestionCategory() {
      try {
        const response = await CRUDQuestionCategory.getAll();
        const categoryIds = response.data.data.map(({ id }) => id);
        await this.loadToCache({ key:CACHE_KEYS.question_categories, ids: categoryIds });
      } catch (e) {
        this.$toast.error(e.response.data.result_message);
      }
    },

    checkIsOverdue(item) {
      if (item.status !== CLIENT_QUESTION_STATUS.PENDING) return {};

      if (differenceInDays(new Date(), new Date(item.created_at)) > 1) {
        return { class: 'red lighten-4' };
      }
      return {};
    },

    statusColor(status) {
      return {
        [CLIENT_QUESTION_STATUS.PENDING]: 'error',
        [CLIENT_QUESTION_STATUS.ACCEPTED]: 'primary',
        [CLIENT_QUESTION_STATUS.ANSWERED]: 'success',
        [CLIENT_QUESTION_STATUS.CLOSED]: 'secondary',
        [CLIENT_QUESTION_STATUS.AWAITING]: 'accent',
      }[status];
    },
  },
};
</script>

<style lang="scss">
.theme--light .v-data-table-header th {
  min-width: auto;
}

.v-dialog.v-dialog--active {
  overflow-y: visible;
}

.close {
  position: absolute;
  right: -20px;
  top: -15px;
}

.v-data-table__wrapper {
  table {
    th:nth-child(n + 3) {
      min-width: 200px;
    }
  }
}

.cell {
  &-error {
    color: #f84283;
    font-weight: bold;
  }
}
.reset-button {
  position: absolute;
  right: 10px;
  top: 10px;
}
</style>
